import { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Inbox } from 'lucide-react';
import { getSystemNotifications, markAllSystemNotificationsRead, markSystemNotificationRead, type SystemNotification } from '../lib/api';
import './NotificationCenter.css';

type NotificationTab = 'unread' | 'all';

export default function NotificationCenter() {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<NotificationTab>('unread');
  const [notifications, setNotifications] = useState<SystemNotification[]>([]);
  const [loadError, setLoadError] = useState('');
  const root = useRef<HTMLDivElement>(null);
  const unreadCount = notifications.filter((notification) => !notification.read_at).length;
  const visibleNotifications = tab === 'unread' ? notifications.filter((notification) => !notification.read_at) : notifications;

  const refreshNotifications = async () => {
    try {
      const token = await getAccessTokenSilently();
      if (!token) throw new Error('Missing access token');
      setNotifications(await getSystemNotifications(token));
      setLoadError('');
    } catch {
      setLoadError('Unable to load system notices.');
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    void refreshNotifications();
    const interval = window.setInterval(() => void refreshNotifications(), 60000);
    return () => window.clearInterval(interval);
  }, [getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  const markAllRead = async () => {
    if (!unreadCount) return;
    try {
      const token = await getAccessTokenSilently();
      if (!token) throw new Error('Missing access token');
      await markAllSystemNotificationsRead(token);
      setNotifications((current) => current.map((notification) => ({ ...notification, read_at: notification.read_at || new Date().toISOString() })));
    } catch {
      setLoadError('Unable to mark notifications as read.');
    }
  };

  const openNotification = async (notification: SystemNotification) => {
    if (!notification.read_at) {
      try {
        const token = await getAccessTokenSilently();
        if (!token) throw new Error('Missing access token');
        await markSystemNotificationRead(token, notification.id);
        setNotifications((current) => current.map((item) => item.id === notification.id ? { ...item, read_at: new Date().toISOString() } : item));
      } catch {
        setLoadError('Unable to mark notification as read.');
      }
    }
    setOpen(false);
    if (notification.href) navigate(notification.href);
  };

  return <div className="notification-center" ref={root}>
    <button type="button" className={`icon-button notification-trigger ${open ? 'active' : ''}`} onClick={() => setOpen((current) => !current)} aria-label="Notifications" aria-expanded={open} aria-controls="notification-popup" title="Notifications">
      <Bell size={19} />
    </button>
    {open && <section className="notification-popup" id="notification-popup" role="dialog" aria-label="Notifications">
      <header className="notification-popup-header">
        <div><h2>Notifications</h2><p>{unreadCount ? `${unreadCount} unread notice${unreadCount === 1 ? '' : 's'}` : 'Your queue is clear'}</p></div>
        <button type="button" className="notification-mark-read" disabled={!unreadCount} onClick={() => void markAllRead()}><CheckCheck size={15} /> Mark all read</button>
      </header>
      <div className="notification-tabs" role="tablist" aria-label="Notification filter">
        <button type="button" role="tab" aria-selected={tab === 'unread'} className={tab === 'unread' ? 'active' : ''} onClick={() => setTab('unread')}>Unread</button>
        <button type="button" role="tab" aria-selected={tab === 'all'} className={tab === 'all' ? 'active' : ''} onClick={() => setTab('all')}>All</button>
      </div>
      {loadError && <p className="notification-load-error" role="alert">{loadError}</p>}
      {visibleNotifications.length ? <div className="notification-items" role="tabpanel">{visibleNotifications.map((notification) => <button type="button" key={notification.id} className={`notification-item ${notification.read_at ? 'read' : 'unread'}`} onClick={() => void openNotification(notification)}><span className="notification-item-indicator" /><span className="notification-item-copy"><strong>{notification.title}</strong><span>{notification.body}</span><time dateTime={notification.created_at}>{new Date(notification.created_at).toLocaleString()}</time></span></button>)}</div> : <div className="notification-empty-state" role="tabpanel"><span className="notification-empty-icon"><Inbox size={25} strokeWidth={1.8} /></span><strong>{loadError ? 'Notifications unavailable' : tab === 'unread' ? 'No unread notifications' : 'No notifications yet'}</strong><p>{loadError ? 'Try again shortly.' : 'Visit reminders and other system notices will appear here.'}</p></div>}
    </section>}
  </div>;
}
